import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, BusinessCardInput } from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import { Save, Eye, EyeOff, Download, Plus, X, QrCode, BarChart3, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';

export function CardConfigPage() {
  const queryClient = useQueryClient();
  const token = localStorage.getItem('token') || '';

  // Form state
  const [formData, setFormData] = useState<any>({
    displayName: '',
    romajiName: '',
    title: '',
    bio: '',
    photoUrl: '',
    email: '',
    phone: '',
    officeLocation: '',
    workingHours: '',
    theme: 'MODERN',
    isVisible: true,
    socialLinks: {
      linkedin: '',
      twitter: '',
      github: '',
      website: '',
      calendar: '',
    },
    serviceLinks: [],
    visibilitySettings: {
      email: true,
      phone: true,
      officeLocation: true,
      workingHours: true,
      socialLinks: true,
      serviceLinks: true,
    },
  });

  const [showQR, setShowQR] = useState(false);
  const [uploadingServiceLinkIndex, setUploadingServiceLinkIndex] = useState<number | null>(null);
  const [regenerateSlug, setRegenerateSlug] = useState(false);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Fetch my card
  const { data: card, isLoading } = useQuery({
    queryKey: ['myCard'],
    queryFn: () => api.getMyCard(token),
    enabled: !!token,
  });

  // Update card mutation
  const updateMutation = useMutation({
    mutationFn: (data: BusinessCardInput) => api.updateCard(token, data),
    onSuccess: () => {
      setRegenerateSlug(false);
      queryClient.invalidateQueries({ queryKey: ['myCard'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      alert('Card updated successfully!');
    },
    onError: (error: Error) => {
      setRegenerateSlug(false);
      alert(`Failed to update: ${error.message}`);
    },
  });

  // Fetch analytics
  const { data: analytics } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => api.getAnalytics(token, 30),
    enabled: !!card,
  });

  // Load card data
  useEffect(() => {
    if (card) {
      setFormData({
        displayName: card.displayName || '',
        romajiName: card.romajiName || '',
        title: card.title || '',
        bio: card.bio || '',
        photoUrl: card.photoUrl || '',
        email: card.email || '',
        phone: card.phone || '',
        officeLocation: card.officeLocation || '',
        workingHours: card.workingHours || '',
        theme: card.theme,
        isVisible: card.isVisible,
        socialLinks: card.socialLinks || {
          linkedin: '',
          twitter: '',
          github: '',
          website: '',
          calendar: '',
        },
        serviceLinks: card.serviceLinks || [],
        visibilitySettings: card.visibilitySettings || {
          email: true,
          phone: true,
          officeLocation: true,
          workingHours: true,
          socialLinks: true,
          serviceLinks: true,
        },
      });
    }
  }, [card]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData = regenerateSlug ? { ...formData, regenerateSlug: true } : formData;
    updateMutation.mutate(submitData);
  };

  const handleRegenerateSlug = () => {
    setRegenerateSlug(true);
    // Auto-submit to regenerate the slug
    const submitData = { ...formData, regenerateSlug: true };
    updateMutation.mutate(submitData);
  };

  const addServiceLink = () => {
    setFormData({
      ...formData,
      serviceLinks: [...formData.serviceLinks, { title: '', url: '', icon: '🔗', description: '', imageUrl: '', displayType: 'iframe' as const }]
    });
  };

  const updateServiceLink = (index: number, field: string, value: string) => {
    const updated = [...formData.serviceLinks];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, serviceLinks: updated });
  };

  const removeServiceLink = (index: number) => {
    setFormData({
      ...formData,
      serviceLinks: formData.serviceLinks.filter((_, i) => i !== index)
    });
  };

  const handleServiceLinkImageUpload = async (index: number, file: File) => {
    const acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!acceptedTypes.includes(file.type)) {
      alert('Please upload PNG, JPG, GIF, or WebP images.');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('Maximum file size is 5MB.');
      return;
    }

    try {
      setUploadingServiceLinkIndex(index);

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      updateServiceLink(index, 'imageUrl', data.url);
    } catch (error) {
      alert('Failed to upload image: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUploadingServiceLinkIndex(null);
    }
  };

  const handleServiceLinkDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleServiceLinkDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleServiceLinkImageUpload(index, file);
    }
  };

  const triggerServiceLinkFileInput = (index: number) => {
    fileInputRefs.current[index]?.click();
  };

  const handleServiceLinkFileSelect = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (file) {
      handleServiceLinkImageUpload(index, file);
    }
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const publicUrl = `${window.location.origin}/${formData.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">My Business Card</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowQR(!showQR)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <QrCode className="w-4 h-4" />
              {showQR ? 'Hide QR' : 'Show QR'}
            </button>
            {card && (
              <a
                href={`/${card.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <Eye className="w-4 h-4" />
                View
              </a>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h2 className="text-lg font-semibold">Basic Information</h2>

              <div>
                <label className="block text-sm font-medium mb-1">Display Name *</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Romaji Name (for URL)</label>
                <input
                  type="text"
                  value={formData.romajiName || ''}
                  onChange={(e) => setFormData({ ...formData, romajiName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., tanaka-yuki"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for your public URL. Leave empty to use Display Name.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Software Engineer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Short professional bio..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Photo URL</label>
                <input
                  type="url"
                  value={formData.photoUrl}
                  onChange={(e) => setFormData({ ...formData, photoUrl: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h2 className="text-lg font-semibold">Contact Information</h2>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Office Location</label>
                <input
                  type="text"
                  value={formData.officeLocation}
                  onChange={(e) => setFormData({ ...formData, officeLocation: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Tokyo Office, Floor 5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Working Hours</label>
                <input
                  type="text"
                  value={formData.workingHours}
                  onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Mon-Fri 9:00-18:00 JST"
                />
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h2 className="text-lg font-semibold">Social Links</h2>

              <div>
                <label className="block text-sm font-medium mb-1">LinkedIn</label>
                <input
                  type="url"
                  value={formData.socialLinks.linkedin}
                  onChange={(e) => setFormData({
                    ...formData,
                    socialLinks: { ...formData.socialLinks, linkedin: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Twitter/X</label>
                <input
                  type="url"
                  value={formData.socialLinks.twitter}
                  onChange={(e) => setFormData({
                    ...formData,
                    socialLinks: { ...formData.socialLinks, twitter: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">GitHub</label>
                <input
                  type="url"
                  value={formData.socialLinks.github}
                  onChange={(e) => setFormData({
                    ...formData,
                    socialLinks: { ...formData.socialLinks, github: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input
                  type="url"
                  value={formData.socialLinks.website}
                  onChange={(e) => setFormData({
                    ...formData,
                    socialLinks: { ...formData.socialLinks, website: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Calendar</label>
                <input
                  type="url"
                  value={formData.socialLinks.calendar}
                  onChange={(e) => setFormData({
                    ...formData,
                    socialLinks: { ...formData.socialLinks, calendar: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Calendly or booking link"
                />
              </div>
            </div>

            {/* Service Links */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Service Links</h2>
                <button
                  type="button"
                  onClick={addServiceLink}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {formData.serviceLinks.map((link, index) => (
                <div key={index} className="border rounded-xl overflow-hidden">
                  {/* Header with icon, title, delete */}
                  <div className="flex items-center gap-3 p-4 bg-gray-50">
                    <input
                      type="text"
                      value={link.icon}
                      onChange={(e) => updateServiceLink(index, 'icon', e.target.value)}
                      className="w-12 text-center text-lg border border-gray-300 rounded-lg"
                      placeholder="🔗"
                    />
                    <input
                      type="text"
                      value={link.title}
                      onChange={(e) => updateServiceLink(index, 'title', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-medium"
                      placeholder="Link title"
                    />
                    <button
                      type="button"
                      onClick={() => removeServiceLink(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Body with fields */}
                  <div className="p-4 space-y-3">
                    {/* URL */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">URL</label>
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => updateServiceLink(index, 'url', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="https://..."
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Description (optional)</label>
                      <input
                        type="text"
                        value={link.description || ''}
                        onChange={(e) => updateServiceLink(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Short description of this link..."
                      />
                    </div>

                    {/* Display Type */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Preview Type</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => updateServiceLink(index, 'displayType', 'iframe')}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            (link.displayType || 'iframe') === 'iframe'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Live Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => updateServiceLink(index, 'displayType', 'image')}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            link.displayType === 'image'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Custom Image
                        </button>
                      </div>
                    </div>

                    {/* Image Upload (when Custom Image is selected) */}
                    {link.displayType === 'image' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Preview Image</label>
                        <div
                          className={`mt-1 border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer ${
                            uploadingServiceLinkIndex === index
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                          }`}
                          onDragOver={handleServiceLinkDragOver}
                          onDrop={(e) => handleServiceLinkDrop(e, index)}
                          onClick={() => triggerServiceLinkFileInput(index)}
                        >
                          {link.imageUrl ? (
                            <div className="flex items-center gap-4">
                              <img
                                src={link.imageUrl}
                                alt="Preview"
                                className="h-16 w-16 rounded-lg object-cover border border-gray-200"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">Image uploaded</p>
                                <p className="text-xs text-gray-500 truncate">{link.imageUrl}</p>
                              </div>
                              <button
                                type="button"
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                                disabled={uploadingServiceLinkIndex === index}
                                onClick={(e) => { e.stopPropagation(); triggerServiceLinkFileInput(index); }}
                              >
                                {uploadingServiceLinkIndex === index ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Change
                                  </>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              {uploadingServiceLinkIndex === index ? (
                                <div className="flex items-center justify-center gap-2">
                                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                                  <span className="text-sm text-gray-500">Uploading...</span>
                                </div>
                              ) : (
                                <>
                                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                                    <ImageIcon className="h-5 w-5 text-gray-400" />
                                  </div>
                                  <p className="text-sm text-gray-600 mb-1">
                                    Drag & drop an image here
                                  </p>
                                  <p className="text-xs text-gray-400 mb-3">
                                    PNG, JPG, GIF or WebP up to 5MB
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                          <input
                            ref={(el) => fileInputRefs.current[index] = el}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            className="hidden"
                            onChange={(e) => handleServiceLinkFileSelect(e, index)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Appearance */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h2 className="text-lg font-semibold">Appearance</h2>

              <div>
                <label className="block text-sm font-medium mb-2">Theme</label>
                <div className="grid grid-cols-4 gap-2">
                  {(['MODERN', 'CLASSIC', 'BOLD', 'MINIMAL'] as const).map((theme) => (
                    <button
                      key={theme}
                      type="button"
                      onClick={() => setFormData({ ...formData, theme })}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                        formData.theme === theme
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Visibility Settings */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4">
              <h2 className="text-lg font-semibold">Visibility Settings</h2>

              <div className="grid grid-cols-2 gap-4">
                {Object.entries(formData.visibilitySettings).map(([key, value]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value as boolean}
                      onChange={(e) => setFormData({
                        ...formData,
                        visibilitySettings: {
                          ...formData.visibilitySettings,
                          [key]: e.target.checked
                        }
                      })}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Card Status */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Card Status</h2>
                  <p className="text-sm text-gray-600">
                    {formData.isVisible ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <Eye className="w-4 h-4" /> Public and visible
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <EyeOff className="w-4 h-4" /> Disabled
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isVisible: !formData.isVisible })}
                  className={`px-4 py-2 rounded-lg ${
                    formData.isVisible
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {formData.isVisible ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>

            {/* Save */}
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </form>

          {/* Preview / QR */}
          <div className="space-y-6">
            {showQR && card && (
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <h3 className="text-lg font-semibold mb-4">QR Code</h3>
                <div className="bg-white p-4 rounded-lg inline-block">
                  <QRCodeSVG value={`${window.location.origin}/${card.slug}`} size={200} />
                </div>
                <p className="text-sm text-gray-600 mt-4">{`${window.location.origin}/${card.slug}`}</p>
                <button
                  onClick={() => {
                    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
                    if (canvas) {
                      const url = canvas.toDataURL('image/png');
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'business-card-qr.png';
                      a.click();
                    }
                  }}
                  className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Download className="w-4 h-4" />
                  Download QR
                </button>
              </div>
            )}

            {/* Public URL */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="text-lg font-semibold mb-2">Public URL</h3>
              <p className="text-sm text-gray-600 break-all mb-3">{card ? `${window.location.origin}/${card.slug}` : publicUrl}</p>
              <button
                type="button"
                onClick={handleRegenerateSlug}
                disabled={updateMutation.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Regenerate URL from Romaji Name
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Updates your public URL based on the Romaji Name field above.
              </p>
            </div>

            {/* Analytics */}
            {analytics && (
              <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Analytics (30 days)</h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{analytics.total}</p>
                    <p className="text-sm text-gray-600">Total Views</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-green-600 flex items-center justify-center gap-1">
                      {analytics.total > 0 ? '+' : ''}
                      {analytics.total}
                    </p>
                    <p className="text-sm text-gray-600">This Month</p>
                  </div>
                </div>

                {analytics.recent.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Recent Views</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {analytics.recent.slice(0, 10).map((view: any) => (
                        <div key={view.id} className="text-xs border-b pb-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {new Date(view.viewedAt).toLocaleDateString()}
                            </span>
                            <span className="text-gray-500">
                              {new Date(view.viewedAt).toLocaleTimeString()}
                            </span>
                          </div>
                          {view.referer && (
                            <p className="text-gray-500 truncate">
                              From: {view.referer.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
